import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { LEAD_TYPES } from '@insurensa/shared';
import { Card, PageTitle, StatCard } from '@insurensa/ui';
import { useFormat, useT } from '@insurensa/i18n';
import { api } from '../lib/api.js';
import { ErrorAlert } from '../components/ui.js';

interface Dashboard {
  users: { total: number; last30Days: number };
  partners: { pending: number; approved: number };
  leads: { total: number; last30Days: number; byType: Record<string, number> };
  offers: { total: number; accepted: number; conversionRate: number };
  moderation: { reviewsPending: number };
}

/** Replaces Masterdata/dashboard.php. */
export function DashboardPage(): JSX.Element {
  const t = useT();
  const fmt = useFormat();

  const { data, error, isLoading } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => api.get<Dashboard>('/admin/dashboard'),
  });

  const show = (value: number | undefined): string => (isLoading ? '—' : String(value ?? 0));

  return (
    <>
      <PageTitle
        heading={t('admin.nav.dashboard')}
        subheading={t('admin.dashboard.subheading')}
        icon="pe-7s-graph2"
      />

      <ErrorAlert error={error} />

      <div className="row">
        <div className="col-md-6 col-xl-3">
          <StatCard
            label={t('admin.dashboard.users')}
            value={show(data?.users.total)}
            meta={data ? t('common.inLast30Days', { count: data.users.last30Days }) : undefined}
            tone="primary"
            icon="pe-7s-users"
          />
        </div>
        <div className="col-md-6 col-xl-3">
          <StatCard
            label={t('admin.dashboard.leads')}
            value={show(data?.leads.total)}
            meta={data ? t('common.inLast30Days', { count: data.leads.last30Days }) : undefined}
            tone="info"
            icon="pe-7s-note2"
          />
        </div>
        <div className="col-md-6 col-xl-3">
          <StatCard
            label={t('admin.dashboard.offers')}
            value={show(data?.offers.total)}
            meta={data ? t('admin.dashboard.accepted', { percent: data.offers.conversionRate }) : undefined}
            tone="success"
            icon="pe-7s-cash"
          />
        </div>
        <div className="col-md-6 col-xl-3">
          <StatCard
            label={t('admin.dashboard.activePartners')}
            value={show(data?.partners.approved)}
            meta={data ? t('admin.dashboard.awaitingReview', { count: data.partners.pending }) : undefined}
            tone="warning"
            icon="pe-7s-culture"
          />
        </div>
      </div>

      <div className="row">
        <div className="col-lg-6">
          <Card
            title={<div className="card-header-title">{t('admin.dashboard.leadsByLine')}</div>}
            noPadding
          >
            <table className="table table-hover table-striped mb-0">
              <tbody>
                {LEAD_TYPES.map((type) => (
                  <tr key={type}>
                    <td>{fmt.leadType(type)}</td>
                    <td className="text-right" style={{ width: 100 }}>
                      <strong>{data?.leads.byType[type] ?? 0}</strong>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </div>

        <div className="col-lg-6">
          <Card title={<div className="card-header-title">{t('admin.dashboard.openTasks')}</div>}>
            <TaskRow
              label={t('admin.dashboard.reviewRegistrations')}
              count={data?.partners.pending ?? 0}
              to="/partners?status=PENDING"
            />
            <TaskRow
              label={t('admin.dashboard.moderateReviews')}
              count={data?.moderation.reviewsPending ?? 0}
              to="/reviews?status=PENDING"
            />
          </Card>
        </div>
      </div>
    </>
  );
}

function TaskRow({ label, count, to }: { label: string; count: number; to: string }): JSX.Element {
  const t = useT();

  return (
    <div className="d-flex justify-content-between align-items-center py-2">
      <span>{label}</span>
      {count > 0 ? (
        <Link to={to} className="btn btn-primary btn-sm">
          {t('common.openCount', { count })}
        </Link>
      ) : (
        <div className="badge badge-success badge-pill">{t('common.done')}</div>
      )}
    </div>
  );
}
